import PlaylistItem from '../../models/playlistItem/playlistItem';
import Track from '../../models/track/track';
import { TrackProperty } from '../../services/columnSettings/playlistColumns';
import { ILibraryContext } from '../../services/library/libraryService';
import { matchesTrackQuery, parseTrackQuery } from '../../utilities/trackSearch';

/** Shared identity predicate, so an empty filter does not allocate one per call. */
const matchEverything = () => true;

export interface ISorter<T extends PlaylistItem | Track> {
  propName?: string;
  (a: T, b: T): number;
}

export const sortByIndex = function <T extends PlaylistItem | Track>(): ISorter<T> {
  const sorter = (_a: T, _b: T) => 0;
  sorter.propName = 'index';
  return sorter;
};
export const sortByIndexDescending = function <T extends PlaylistItem | Track>(): ISorter<T> {
  const sorter = (_a: T, _b: T) => -1;
  sorter.propName = 'index-descending';
  return sorter;
};

/**
 * What breaks a tie once the clicked column compares equal.
 *
 * Sorting by artist alone leaves each artist's tracks in whatever order the
 * playlist had them, which is never what anyone wants: the expectation is the
 * albums in order and each album in playing order. So every column that
 * groups tracks carries the rest of the natural album order behind it.
 *
 * The tie-breakers always run ascending, even when the primary key is
 * descending - "artist Z to A" should still list each album from track 1.
 *
 * Album deliberately does not go through the artist: two albums sharing a
 * title interleave by disc and track number, and whatever order the playlist
 * already had settles the rest, since the sort is stable.
 *
 * Columns absent here (title, length, type, date added, track number,
 * uploader and the other tag fields) fall back to the one key the user
 * clicked, since nothing groups under them.
 */
export const sortTieBreakers: Partial<Record<TrackProperty, readonly TrackProperty[]>> = {
  [TrackProperty.artist]: [TrackProperty.album, TrackProperty.disc, TrackProperty.trackNumber],
  [TrackProperty.album]: [TrackProperty.disc, TrackProperty.trackNumber],
  [TrackProperty.genre]: [
    TrackProperty.artist,
    TrackProperty.album,
    TrackProperty.disc,
    TrackProperty.trackNumber,
  ],
  [TrackProperty.releaseDate]: [
    TrackProperty.artist,
    TrackProperty.album,
    TrackProperty.disc,
    TrackProperty.trackNumber,
  ],
};

/**
 * The one comparison every sort in the app makes, on a single value:
 * undefined sorts last whichever direction you are going - every `>` and
 * `<` against it is false, so without this the sorter would call each such
 * pair equal - and strings compare case-insensitively.
 *
 * The tag-derived keys are optional - a track whose tags have never been
 * read has no track number at all - which is where the undefined rule earns
 * its keep. The streaming search results sort with it too.
 */
export function compareSortValues(
  aVal: string | number | undefined,
  bVal: string | number | undefined,
) {
  if (aVal === undefined || bVal === undefined) {
    return aVal === bVal ? 0 : aVal === undefined ? 1 : -1;
  }

  if (typeof aVal === 'string') {
    var aProp: string | number = aVal.toLowerCase();
    var bProp: string | number = (bVal as string).toLowerCase();
  } else {
    var aProp: string | number = aVal;
    var bProp: string | number = bVal as number;
  }

  return aProp > bProp ? 1 : aProp < bProp ? -1 : 0;
}

/** Compares one key of two library tracks. */
export function compareTrackKey(aTrack: Track, bTrack: Track, key: TrackProperty): number {
  return compareSortValues(aTrack[key], bTrack[key]);
}

/**
 * Compares two library tracks on `prop`, then on its tie-breakers in order.
 *
 * Only the primary comparison is flipped for a descending sort - see
 * sortTieBreakers for why.
 */
export function compareTracks(
  aTrack: Track,
  bTrack: Track,
  prop: TrackProperty,
  descending = false,
): number {
  const primary = compareTrackKey(aTrack, bTrack, prop);
  if (primary !== 0) return descending ? -primary : primary;

  const tieBreakers = sortTieBreakers[prop];
  if (!tieBreakers) return 0;

  for (const key of tieBreakers) {
    const result = compareTrackKey(aTrack, bTrack, key);
    if (result !== 0) return result;
  }

  return 0;
}

function createTrackResolver<T extends PlaylistItem | Track>(libCtx: ILibraryContext) {
  return function resolveTrack(item: T): Track {
    return (item as Track).type ? (item as Track) : libCtx.getLibraryTrack(item as PlaylistItem);
  };
}

export function createSortByProp<T extends PlaylistItem | Track>(libCtx: ILibraryContext) {
  const resolveTrack = createTrackResolver<T>(libCtx);
  return function sortByProp(prop: TrackProperty) {
    const sorter = function (a: T, b: T): number {
      return compareTracks(resolveTrack(a), resolveTrack(b), prop);
    };

    sorter.propName = prop;

    return () => sorter;
  };
}

export function createSortByPropDescending<T extends PlaylistItem | Track>(
  libCtx: ILibraryContext,
) {
  const resolveTrack = createTrackResolver<T>(libCtx);
  return function sortByPropDescending(prop: TrackProperty) {
    const sorter = function (a: T, b: T): number {
      return compareTracks(resolveTrack(a), resolveTrack(b), prop, true);
    };

    sorter.propName = `${prop}-descending`;

    return () => sorter;
  };
}

/**
 * Builds the playlist filter predicate.
 *
 * The parsing is done once per query here rather than once per track inside
 * the predicate - a 40k library calls the predicate 40,000 times per
 * keystroke, and the query has not changed between any two of them.
 *
 * See utilities/trackSearch.ts for the query syntax.
 */
export function createFilterBy<T extends PlaylistItem | Track>(libCtx: ILibraryContext) {
  return function filterBy(filter: string | undefined) {
    const clauses = parseTrackQuery(filter);

    if (!clauses.length) return matchEverything as (item: T) => boolean;

    return function (plItm: T) {
      if ((plItm as Track).type) {
        var track = plItm as Track;
      } else {
        var track = libCtx.getLibraryTrack(plItm as PlaylistItem);
      }

      return matchesTrackQuery(track, clauses);
    };
  };
}
